import React, { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import FleetGeofenceEditor from "@src/settings/geofence/components/FleetGeofenceEditor"
import { getFleetsUuidFences, putFleetsUuidFenceId } from "@src/api"
import { fencingArrtoCoordinates, fencingPostStrToArr } from "@src/utils/coordinates"
import type { FENCE } from "@src/settings/geofence/types"
import { showToastMessage } from "@src/toast/useToast"
import { Page } from "@src/components"

export const EditFleetGeofenceScreen: React.FC = () => {
  const { t } = useTranslation()
  const { partnerUuid, fleetUuid, fenceUuid } = useParams()
  const [searchParams] = useSearchParams()
  const fleetName = searchParams.get("fleetName") || "-"
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [fence, setFence] = useState<FENCE | null>(null)

  useEffect(() => {
    const load = async () => {
      if (!partnerUuid || !fleetUuid || !fenceUuid) return
      const { response, data } = await getFleetsUuidFences(partnerUuid, fleetUuid)
      if (response.ok && Array.isArray(data?.fences)) {
        const id = parseInt(fenceUuid, 10)
        const found = data.fences.find((f) => f.id === id) || null
        setFence(found)
      }
      setLoading(false)
    }
    load()
  }, [partnerUuid, fleetUuid, fenceUuid])

  const initialName = fence?.name ?? ""
  const initialCoordinates = useMemo(() => {
    if (!fence) return []
    const posts = fence.fencingposts
    return typeof posts === "string" ? fencingPostStrToArr(posts) : fencingArrtoCoordinates(posts)
  }, [fence])

  if (loading) {
    return (
      <Page isSettings showHeader headerValue={t("settings.fence.edit_fence")}>
        <div className="p-3">{t("common.loading")}</div>
      </Page>
    )
  }
  if (!fence || !partnerUuid || !fleetUuid) {
    return (
      <Page isSettings showHeader headerValue={t("settings.fence.edit_fence")}>
        <div className="p-3 text-danger">{t("settings.fence.not_found")}</div>
      </Page>
    )
  }

  return (
    <FleetGeofenceEditor
      header={t("settings.fence.edit_fence")}
      actionLabel={t("settings.fence.update")}
      initialName={initialName}
      initialCoordinates={initialCoordinates}
      onSubmit={async (name, coordinates) => {
        const stringPaths = coordinates.map((latLng) => `[${latLng.lat}, ${latLng.lng}]`)
        const body = { fence: { kind: "classic", fencingposts: `[${stringPaths}]`, name } }
        const id = parseInt(fenceUuid!, 10)
        const { response } = await putFleetsUuidFenceId(partnerUuid, fleetUuid, id, body)
        if (response.ok) {
          const title = t("settings.fence.toast.edit_title")
          const text = t("settings.fence.toast.edit_text", { name, fleet: fleetName })
          showToastMessage(title, text, true)
          navigate(`/${partnerUuid}/settings/geofences`)
        } else {
          const title = t("settings.fence.toast.edit_title")
          const text = t("settings.fence.validate.error_edit")
          showToastMessage(title, text, true)
        }
      }}
    />
  )
}

export default EditFleetGeofenceScreen
